import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { REHYDRATE } from 'redux-persist';
import {
  normalizeSavedSearchUrlInput,
  shouldSkipPersistingSavedSearchUrl,
  toStoredSearchUrl,
} from '../../utils/saved-searches/normalize-saved-search-url';
import { distillSearchHistory } from '../../utils/saved-searches/search-history-policy';

export interface SearchEntry {
  url: string;
  timestamp: number;
  /**
   * Deliberately saved by the user (alert CTA, accepted suggestion) rather than passively
   * recorded by the SRP. Pinned entries are never collapsed as transitional steps and win over an
   * auto-recorded entry for the same search (see utils/saved-searches/search-history-policy.ts).
   */
  pinned?: boolean;
}

export interface AddSearchPayload {
  url: string;
  pinned?: boolean;
}

interface SavedSearchesState {
  /** Single list: both auto-added (on search) and manually saved searches, most recent first. */
  savedSearches: SearchEntry[];
}

const initialState: SavedSearchesState = {
  savedSearches: [],
};

const MAX_SAVED_SEARCHES = 50;

/**
 * Applies the search-history policy to a raw list: drops non-searches, collapses transitional
 * steps and same-search variants, newest first, capped. Every write path ends here so the stored
 * list never holds what the hub would have to hide.
 */
function normalizeSearchEntryList(entries: SearchEntry[]): SearchEntry[] {
  const cleaned: SearchEntry[] = [];
  for (const entry of entries) {
    if (entry?.url == null || typeof entry.url !== 'string') continue;
    const norm = normalizeSavedSearchUrlInput(entry.url);
    if (!norm) continue;
    if (shouldSkipPersistingSavedSearchUrl(norm)) continue;
    cleaned.push({ url: norm, timestamp: entry.timestamp, ...(entry.pinned ? { pinned: true } : {}) });
  }
  return distillSearchHistory(cleaned).slice(0, MAX_SAVED_SEARCHES);
}

const savedSearchesSlice = createSlice({
  name: 'recentSearches',
  initialState,
  reducers: {
    /**
     * Records a search (by URL). Used both when the SRP settles on a new query/filter state and
     * when the user deliberately saves one (`pinned: true`). The URL is stored in its
     * `toStoredSearchUrl` form and the whole list is re-distilled, so a refinement of the search
     * the user was just on replaces it rather than sitting next to it, a re-run of an existing
     * search only bumps its timestamp, and a non-search URL is ignored.
     */
    addSearch: (state, action: PayloadAction<string | AddSearchPayload>) => {
      if (!state.savedSearches) state.savedSearches = [];
      const payload = typeof action.payload === 'string' ? { url: action.payload } : action.payload;
      const url = toStoredSearchUrl(payload.url);
      if (shouldSkipPersistingSavedSearchUrl(url)) return;
      const incoming: SearchEntry = { url, timestamp: Date.now(), ...(payload.pinned ? { pinned: true } : {}) };
      state.savedSearches = normalizeSearchEntryList([incoming, ...state.savedSearches]);
    },
    /**
     * Replaces one entry's URL with another while keeping its place in the list. Used by
     * useReplaceSavedSearch (edit filters / apply fix on the hub) where the old URL must go away
     * even if the user had dwelt on it for a long time.
     */
    refineSearch: (state, action: PayloadAction<{ previousUrl: string; url: string }>) => {
      if (!state.savedSearches) state.savedSearches = [];
      const url = normalizeSavedSearchUrlInput(action.payload.url);
      if (shouldSkipPersistingSavedSearchUrl(url)) return;
      const previousUrl = normalizeSavedSearchUrlInput(action.payload.previousUrl);
      const previous = state.savedSearches.find((s) => s.url === previousUrl);
      const withoutPrevious = state.savedSearches.filter((s) => s.url !== previousUrl);
      const replaced: SearchEntry = { url, timestamp: Date.now(), ...(previous?.pinned ? { pinned: true } : {}) };
      state.savedSearches = normalizeSearchEntryList([replaced, ...withoutPrevious]);
    },
    removeSearch: (state, action: PayloadAction<string>) => {
      if (!state.savedSearches) state.savedSearches = [];
      const needle = normalizeSavedSearchUrlInput(action.payload);
      state.savedSearches = state.savedSearches.filter(
        (s) => normalizeSavedSearchUrlInput(s.url) !== needle,
      );
    },
    setSavedSearches: (state, action: PayloadAction<SearchEntry[]>) => {
      state.savedSearches = Array.isArray(action.payload) ? action.payload : [];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(REHYDRATE, (state, action: any) => {
      const persisted = action.payload?.recentSearches;
      if (!persisted) {
        if (!state.savedSearches) state.savedSearches = [];
        return state;
      }
      // Migrate from old two-list format: merge saved + recent, dedupe by URL, keep order by timestamp
      const saved = Array.isArray(persisted.savedSearches) ? persisted.savedSearches : [];
      const recent = Array.isArray(persisted.recentSearches) ? persisted.recentSearches : [];
      const merged: SearchEntry[] = [];
      const byUrl = new Map<string, SearchEntry>();
      [...saved, ...recent].forEach((entry) => {
        const existing = byUrl.get(entry.url);
        if (!existing || entry.timestamp > existing.timestamp) byUrl.set(entry.url, entry);
      });
      merged.push(...Array.from(byUrl.values()));
      state.savedSearches = normalizeSearchEntryList(merged);
      return state;
    });
  },
});

export const { addSearch, refineSearch, removeSearch, setSavedSearches } = savedSearchesSlice.actions;

/** @deprecated Use addSearch. Kept for compatibility. */
export const addRecentSearch = savedSearchesSlice.actions.addSearch;
/** @deprecated Use addSearch. Kept for compatibility. */
export const addSavedSearch = savedSearchesSlice.actions.addSearch;
/** @deprecated Use removeSearch. Kept for compatibility. */
export const removeRecentSearch = savedSearchesSlice.actions.removeSearch;
/** @deprecated Use removeSearch. Kept for compatibility. */
export const removeSavedSearch = savedSearchesSlice.actions.removeSearch;
/** @deprecated No longer used; single list only. */
export const setRecentSearches = setSavedSearches;

export default savedSearchesSlice.reducer;
